import { PackageItemState } from '../types/lender-packages.types';
import { NetsuiteItemState } from '../types/netsuite-items.types';
import { ESS_CONFIG_OPTIONS, BOM_CSV_HEADERS } from '../constants/lender-packages.constants';

/**
 * Input configuration for BOM CSV export.
 */
interface BomCsvExportInput {
  /** Enabled package items with their BOM data */
  packageItems: PackageItemState[];
  /** Client NetSuite items to append to the export */
  netsuiteItems?: NetsuiteItemState[];
  /** LIS# value (same for all rows) */
  lisNumber: string;
  /** Package key (same for all rows) */
  packageKey: string;
  /** ESS config value (same for all rows) */
  essConfig: string;
}

/**
 * Generates CSV content from enabled package items' BOM data.
 * Each bom_item with a non-null bom_sku produces one CSV row.
 *
 * @param input - Export configuration with items and shared field values
 * @returns CSV string with headers and data rows
 */
export const generateBomCsv = (input: BomCsvExportInput): string => {
  const { packageItems, netsuiteItems, lisNumber, packageKey, essConfig } = input;

  const essConfigLabel = ESS_CONFIG_OPTIONS.find(o => o.value === essConfig)?.label || essConfig;

  const rows: string[] = [BOM_CSV_HEADERS.join(',')];

  for (const item of packageItems) {
    if (!item.enabled) continue;
    for (const bomItem of item.bom_items) {
      if (!bomItem.enabled || !bomItem.bom_sku) continue;

      const row = [
        escapeCsvField(bomItem.bom_sku),
        String(bomItem.quantity),
        escapeCsvField(lisNumber),
        escapeCsvField(bomItem.bom_description || item.equipment_label),
        escapeCsvField(packageKey),
        escapeCsvField(essConfigLabel),
      ];

      rows.push(row.join(','));
    }
  }

  for (const item of (netsuiteItems || [])) {
    if (!item.enabled || !item.external_id) continue;

    const row = [
      escapeCsvField(item.external_id),
      String(item.quantity),
      escapeCsvField(lisNumber),
      escapeCsvField(item.name),
      escapeCsvField(packageKey),
      escapeCsvField(essConfigLabel),
    ];

    rows.push(row.join(','));
  }

  return rows.join('\n');
};

/**
 * Escapes a CSV field value, wrapping in quotes if it contains commas, quotes, or newlines.
 *
 * @param value - Raw field value
 * @returns Escaped CSV field
 */
const escapeCsvField = (value: string): string => {
  if (!value) return '';
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Generates CAD CSV from enabled package items' BOM data.
 * Columns: Qty, MANUFACTURER, DISPLAY NAME - MODEL, ITEM
 *
 * @param packageItems - Package items with BOM data
 * @returns CSV string with headers and data rows
 */
export const generateCadCsv = (packageItems: PackageItemState[], netsuiteItems?: NetsuiteItemState[]): string => {
  const headers = ['Qty', 'MANUFACTURER', 'DISPLAY NAME - MODEL', 'ITEM'];
  const rows: string[] = [headers.join(',')];

  for (const item of packageItems) {
    if (!item.enabled) continue;
    for (const bomItem of item.bom_items) {
      if (!bomItem.enabled) continue;

      const row = [
        String(bomItem.quantity),
        escapeCsvField(item.make),
        escapeCsvField(bomItem.bom_mpn),
        escapeCsvField(bomItem.bom_description || item.equipment_label),
      ];

      rows.push(row.join(','));
    }
  }

  for (const item of (netsuiteItems || [])) {
    if (!item.enabled || !item.external_id) continue;

    const row = [
      String(item.quantity),
      escapeCsvField(item.manufacturer || ''),
      escapeCsvField(item.display_name),
      escapeCsvField(item.name),
    ];

    rows.push(row.join(','));
  }

  return rows.join('\n');
};

/**
 * Triggers a browser download of the given CSV content.
 *
 * @param csvContent - The CSV string to download
 * @param filename - The download filename
 */
export const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
